import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Bell, Book, Users, CalendarDays, HandHeart, UserRound } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollToTop } from "@/hooks/useScrollToTop";
const Ministry = () => {
  useScrollToTop();
  const features = [{
    icon: Book,
    text: "Sermon & Bible Study Outline Builder",
    color: "bg-[#34495E]"
  }, {
    icon: Users,
    text: "Small Group & Devotional Creator",
    color: "bg-heaven-gold"
  }, {
    icon: CalendarDays,
    text: "Ministry Planning Toolkit",
    color: "bg-[#34495E]"
  }, {
    icon: HandHeart,
    text: "Counseling Resources",
    color: "bg-heaven-gold"
  }, {
    icon: UserRound,
    text: "Youth + Event Planning Assistant",
    color: "bg-[#34495E]"
  }];
  return <div className="min-h-screen bg-heaven-white pt-24 pb-20">
      <div className="container mx-auto px-4 md:px-6">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }}>
            <img src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//new%20hero%20logo.png" alt="Selah Logo" className="h-48 md:h-72 mx-auto mb-8 object-contain" />
            
            <h1 className="text-4xl md:text-6xl font-playfair font-bold text-heaven-black mb-6">
              Selah for Leaders. <span className="text-heaven-gold">Built for Shepherds.</span>
              <br className="mb-10" />
              <span className="text-black mb-10 block italic animate-pulse px-0 py-[47px]">Coming Soon.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              The tools you've prayed for are almost here.
            </p>
            
            <p className="text-lg text-gray-700 mb-12 max-w-4xl mx-auto">
              We're building a ministry suite designed to equip pastors, teachers, small group leaders, 
              counselors, and outreach teams with everything they need to disciple deeply, preach boldly, 
              and shepherd faithfully—in one place.
            </p>
          </motion.div>

          {/* Features Grid */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 max-w-5xl mx-auto">
            {features.slice(0, 3).map((feature, index) => <motion.div key={index} className="group relative overflow-hidden bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300" whileHover={{
            y: -5
          }} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }}>
                <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-heaven-gold/5 opacity-50" />
                <div className="relative p-8 flex flex-col items-center">
                  <div className={`${feature.color} p-4 rounded-full mb-6 transform group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-lg font-medium text-gray-700 group-hover:text-heaven-gold transition-colors duration-300">
                    {feature.text}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>

          {/* Bottom Features Grid - Centered */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="flex justify-center gap-6 mb-16 max-w-5xl mx-auto">
            {features.slice(3).map((feature, index) => <motion.div key={index} className="group relative overflow-hidden bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 w-full max-w-md" whileHover={{
            y: -5
          }} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }}>
                <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-heaven-gold/5 opacity-50" />
                <div className="relative p-8 flex flex-col items-center">
                  <div className={`${feature.color} p-4 rounded-full mb-6 transform group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-lg font-medium text-gray-700 group-hover:text-heaven-gold transition-colors duration-300">
                    {feature.text}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>

          {/* Mission Statement */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="mb-16">
            <p className="text-xl text-gray-700 font-medium mb-4">
              Each tool will be grounded in Scripture, guided by truth, and built with the same Selah mission:
            </p>
            <p className="text-2xl text-heaven-gold font-bold">
              Clarity. Conviction. Christ at the center.
            </p>
          </motion.div>

          {/* Waitlist Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="bg-[#34495E] rounded-2xl p-10 md:p-16 max-w-4xl mx-auto relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center justify-center gap-3">
                <Bell className="h-8 w-8 text-heaven-gold" />
                Want Early Access?
              </h2>
              <p className="text-white/90 mb-8 text-lg">
                Be the first to use Selah's ministry tools and help shape the future of digital discipleship.
              </p>
              <Link to="/sign-up">
                <Button size="lg" className="bg-heaven-gold hover:bg-heaven-dark-gold text-white px-8 py-6 text-lg">
                  Join the Waitlist
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Footer Quote */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.8
        }} className="mt-16 text-gray-600 italic">
            <p className="text-lg">
              "And He gave some to be pastors and teachers… to equip the saints for the work of ministry."
            </p>
            <p className="text-lg font-medium mt-2">— Ephesians 4:11–12</p>
          </motion.div>
        </div>
      </div>
    </div>;
};
export default Ministry;